import { AuthTokenData } from './AuthTokenData';
import { applyAuthContext } from '../../services/network';

function generateSsoUrl(domain: string) {
  return `https://${process.env.REACT_APP_KEYCLOAK_AUTH_DOMAIN_PREFIX}.${domain}/auth`;
}

function generateSilentCheckSsoRedirectUri() {
  return window.location.origin + window.location.pathname + 'silent-check-sso.html';
}

export class AuthManager {
  private readonly domain: string;
  private readonly realm: string;
  private readonly keycloakInitializer: any;
  private keycloak: any = null;
  private authSuccessfulFunctions: Function[] = [];
  private authUnsuccessfulFunctions: Function[] = [];
  private authFailureFunctions: Function[] = [];

  constructor(keycloakInitializer: Function, domain: string, realm: string) {
    this.domain = domain;
    this.realm = realm;
    this.keycloakInitializer = keycloakInitializer;
  }

  initKeycloak(): any {
    const clientId =
      this.realm === 'bitiba'
        ? process.env.REACT_APP_KEYCLOAK_APP_BITIBA_CLIENT_ID
        : process.env.REACT_APP_KEYCLOAK_APP_ZOOPLUS_CLIENT_ID;
    const ssoUrl = generateSsoUrl(this.domain);
    const silentCheckSsoRedirectUri = generateSilentCheckSsoRedirectUri();

    console.log('Initializing Keycloak');
    const keycloak = new this.keycloakInitializer({
      url: ssoUrl,
      realm: this.realm,
      clientId: clientId,
      publicClient: true,
      sslRequired: 'all',
    });

    keycloak
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: silentCheckSsoRedirectUri,
      })
      .then(() => applyAuthContext(keycloak))
      .catch((e: Error) => {
        console.log('Error while initializing Keycloak', e);
        this.authFailureFunctions.forEach((fun) => fun(e?.message));
      });

    keycloak.onReady = (authenticated: boolean) => {
      console.log('Authenticated:', authenticated);
      if (authenticated) {
        const tokenData = new AuthTokenData(keycloak.tokenParsed, keycloak.token);
        this.authSuccessfulFunctions.forEach((fun) => fun(tokenData));
      } else {
        this.authUnsuccessfulFunctions.forEach((fun) => fun());
      }
    };

    keycloak.onTokenExpired = () => {
      console.log('Token is expired, refreshing...');
      keycloak
        .updateToken()
        .then(() => {
          const tokenData = new AuthTokenData(keycloak.tokenParsed, keycloak.token);
          this.authSuccessfulFunctions.forEach((fun) => fun(tokenData));
        })
        .catch((e: Error) => {
          console.log('Error while updating token', e);
          this.authFailureFunctions.forEach((fun) => fun(e.message));
        });
    };

    keycloak.onAuthRefreshError = () => {
      console.log('Error while refreshing keycloak authorization');
      this.authFailureFunctions.forEach((fun) => fun());
    };

    this.keycloak = keycloak;

    return this.keycloak;
  }

  getKeycloak(): any {
    return this.keycloak;
  }

  onAuthenticated(fun: Function) {
    this.authSuccessfulFunctions.push(fun);
    return this;
  }

  onNotAuthenticated(fun: Function) {
    this.authUnsuccessfulFunctions.push(fun);
    return this;
  }

  onAuthenticationFailure(fun: Function) {
    this.authFailureFunctions.push(fun);
    return this;
  }
}
